import React, { useState, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import * as productsCrud from "../../../_redux/productsCrud";

const STATUS_FILTERS = [
  { value: "all", label: "Todos" },
  { value: "ok", label: "OK" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
];

function getStatusType(status, row) {
  if (!status) return "error";
  const s = status.toUpperCase();
  if (s === "OK") {
    if (row && Number(row.stock_riga_farma) !== Number(row.stock_wc)) return "warning";
    return "ok";
  }
  if (s.includes("WARNING")) return "warning";
  return "error";
}

const badgeStyle = { minWidth: "80px", display: "inline-block", textAlign: "center" };

function StatusBadge({ status, skipStockControl, row }) {
  if (skipStockControl === 1) {
    return <Badge variant="success" style={badgeStyle}>Servicio</Badge>;
  }
  const type = getStatusType(status, row);
  const variant = type === "ok" ? "success" : type === "warning" ? "warning" : "danger";
  const label = type === "ok" ? "OK" : type === "warning" ? "Warning" : "Error";
  if (type === "ok") {
    return <Badge variant={variant} style={badgeStyle}>{label}</Badge>;
  }
  return (
    <OverlayTrigger overlay={<Tooltip id="status-tooltip">{status || "SIN ESTADO"}</Tooltip>}>
      <Badge variant={variant} style={{ ...badgeStyle, cursor: "pointer" }}>{label}</Badge>
    </OverlayTrigger>
  );
}

const columns = [
  { dataField: "id", text: "ID" },
  { dataField: "name", text: "Producto" },
  { dataField: "sku", text: "SKU WC" },
  {
    dataField: "riga_sku",
    text: "SKU Riga",
    isDummyField: true,
    formatter: (cell, row) => <span>{row.riga?.sku || "-"}</span>,
  },
  {
    dataField: "riga_farma_gtin",
    text: "GTIN Riga F.",
    isDummyField: true,
    formatter: (cell, row) => <span>{row.riga_farma?.gtin || "-"}</span>,
  },
  {
    dataField: "stock_wc",
    text: "Stock Shop",
    headerClasses: "text-right",
    style: { textAlign: "right" },
    formatter: (cell) => <span>{cell != null ? cell : "-"}</span>,
  },
  {
    dataField: "stock_riga_farma",
    text: "Stock Riga Farma",
    headerClasses: "text-right",
    style: { textAlign: "right" },
    formatter: (cell) => <span>{cell != null ? cell : "-"}</span>,
  },
  {
    dataField: "status",
    text: "Estado",
    formatter: (cell, row) => <StatusBadge status={cell} skipStockControl={row.skip_stock_control} row={row} />,
  },
];

export function ProductsVerify({ activeTab }) {
  const { business_units } = useSelector(
    (state) => ({ business_units: state.helpers.BusinessUnits }),
    shallowEqual
  );

  const [businessUnit, setBusinessUnit] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS[0]);

  const fetchVerify = async (buId) => {
    setLoading(true);
    try {
      const resp = await productsCrud.verifyProducts(buId);
      if (resp && resp.data) {
        setProducts(resp.data.products || []);
        setTotalCount(resp.data.totalCount || 0);
      }
    } catch (err) {
      setProducts([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === "verificacion" && businessUnit) {
      fetchVerify(businessUnit.value);
    }
  }, [activeTab, businessUnit]);

  const counts = products.reduce(
    (acc, p) => {
      if (p.skip_stock_control === 1) {
        acc.servicio += 1;
      } else {
        const type = getStatusType(p.status, p);
        acc[type] = (acc[type] || 0) + 1;
      }
      return acc;
    },
    { ok: 0, warning: 0, error: 0, servicio: 0 }
  );

  const filteredProducts =
    statusFilter.value === "all"
      ? products
      : products.filter((p) => getStatusType(p.status, p) === statusFilter.value);

  return (
    <>
      {/* Selector de Business Unit */}
      <div className="form-group row mb-5">
        <div className="col-lg-4">
          <Select
            options={business_units}
            value={businessUnit}
            onChange={(e) => setBusinessUnit(e)}
            placeholder="Seleccionar unidad de negocio..."
          />
          <small className="form-text text-muted">
            Seleccione una <b>Unidad de Negocio</b> para verificar
          </small>
        </div>
        <div className="col-lg-3">
          <Select
            options={STATUS_FILTERS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e)}
          />
          <small className="form-text text-muted">
            Filtrar por <b>Estado</b>
          </small>
        </div>
      </div>

      {/* Resumen */}
      {products.length > 0 && (
        <div className="d-flex mb-5" style={{ gap: "12px" }}>
          <div className="d-flex align-items-center px-4 py-2 rounded" style={{ backgroundColor: "#f3f6f9" }}>
            <span className="font-weight-bold mr-2">Total:</span>
            <span style={{ fontSize: "1rem" }}>{totalCount}</span>
          </div>
          <div className="d-flex align-items-center px-4 py-2 rounded" style={{ backgroundColor: "#f3f6f9" }}>
            <span className="font-weight-bold mr-2">OK:</span>
            <span style={{ fontSize: "1rem" }}>{counts.ok}</span>
          </div>
          <div className="d-flex align-items-center px-4 py-2 rounded" style={{ backgroundColor: "#f3f6f9" }}>
            <span className="font-weight-bold mr-2">Warning:</span>
            <span style={{ fontSize: "1rem" }}>{counts.warning}</span>
          </div>
          <div className="d-flex align-items-center px-4 py-2 rounded" style={{ backgroundColor: "#f3f6f9" }}>
            <span className="font-weight-bold mr-2">Error:</span>
            <span style={{ fontSize: "1rem" }}>{counts.error}</span>
          </div>
          <div className="d-flex align-items-center px-4 py-2 rounded" style={{ backgroundColor: "#f3f6f9" }}>
            <span className="font-weight-bold mr-2">Servicio:</span>
            <span style={{ fontSize: "1rem" }}>{counts.servicio}</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner spinner-primary spinner-lg" />
        </div>
      )}

      {/* Mensaje si no hay BU seleccionada */}
      {!businessUnit && !loading && (
        <div className="text-center text-muted py-10">
          Seleccione una unidad de negocio para verificar el estado de los productos.
        </div>
      )}

      {/* Tabla */}
      {businessUnit && !loading && (
        <BootstrapTable
          wrapperClasses="table-responsive"
          classes="table table-head-custom table-vertical-center overflow-hidden"
          bootstrap4
          keyField="id"
          data={filteredProducts}
          columns={columns}
          bordered={false}
          noDataIndication="No se encontraron productos"
        />
      )}
    </>
  );
}
